// Phase 20 — EUDR compliance schemas (Due Diligence Statement + GeoJSON
// Polygon). Shared between backend Fastify validation and the mobile polygon
// editor so the same constraints apply on both sides.
import { z } from 'zod';
export const EUDR_RISK_LEVELS = ['LOW', 'STANDARD', 'HIGH'];
export const EudrRiskLevelSchema = z.enum(EUDR_RISK_LEVELS);
// GeoJSON Polygon constraints:
//   - exterior ring only (no holes — V1)
//   - closed (first point === last point)
//   - 4..500 points (a triangle = 3 + closing point)
//   - lng/lat ranges respected
//   - area cap ~10 000 ha (Galicia upper bound; safer than allowing planet-wide)
const GEO_MIN_POINTS = 4;
const GEO_MAX_POINTS = 500;
const MAX_AREA_HECTARES = 10_000;
const lngLatPair = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
export const GeoPolygonSchema = z
    .object({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(lngLatPair).min(GEO_MIN_POINTS).max(GEO_MAX_POINTS)).length(1),
})
    .strict()
    .superRefine((p, ctx) => {
    const ring = p.coordinates[0];
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Polygon ring must be closed (first === last point)',
        });
        return;
    }
    // Phase 25 iter-25 (Lote P-3, B8) — self-intersection check. A bowtie
    // or any crossed polygon makes the EUDR area calculation nonsense
    // (the shoelace formula returns the signed difference of the two
    // lobes), and EUDR auditors reject self-intersecting parcels
    // outright. O(n²) brute force — with max 500 points = 125k segment
    // pair comparisons, fast enough server-side.
    if (ringIsSelfIntersecting(ring)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Polygon ring self-intersects',
        });
        return;
    }
    const areaHa = approximateRingAreaHectares(ring);
    if (areaHa > MAX_AREA_HECTARES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Polygon area ${areaHa.toFixed(0)} ha exceeds cap ${MAX_AREA_HECTARES} ha`,
        });
    }
});
/** Exported for testing — checks every pair of non-adjacent segments in
 *  a closed ring for proper intersection. Segments are considered NOT
 *  intersecting when they merely share a single endpoint at a vertex. */
export function ringIsSelfIntersecting(ring) {
    const n = ring.length - 1; // last point repeats the first
    for (let i = 0; i < n; i++) {
        const a1 = ring[i];
        const a2 = ring[i + 1];
        // Start from i + 2 to skip the adjacent segment.
        for (let j = i + 2; j < n; j++) {
            // Last segment loops back to first, so it's adjacent to segment 0
            // — skip that pair too when i === 0.
            if (i === 0 && j === n - 1)
                continue;
            const b1 = ring[j];
            const b2 = ring[j + 1];
            if (segmentsIntersect(a1, a2, b1, b2))
                return true;
        }
    }
    return false;
}
function segmentsIntersect(p1, p2, p3, p4) {
    // Standard CCW-based proper intersection test. Returns true only when
    // the segments cross transversally; collinear / shared-endpoint cases
    // return false so polygons whose vertices touch (but don't cross) pass.
    const d1 = direction(p3, p4, p1);
    const d2 = direction(p3, p4, p2);
    const d3 = direction(p1, p2, p3);
    const d4 = direction(p1, p2, p4);
    return d1 * d2 < 0 && d3 * d4 < 0;
}
function direction(a, b, c) {
    return (c[0] - a[0]) * (b[1] - a[1]) - (b[0] - a[0]) * (c[1] - a[1]);
}
// Spherical-excess approximation for small polygons (km-scale). Accurate
// enough to reject 1000× over-cap polygons without pulling in turf.js.
export function approximateRingAreaHectares(ring) {
    if (ring.length < 4)
        return 0;
    const R = 6_378_137; // earth radius metres
    const toRad = (d) => (d * Math.PI) / 180;
    let total = 0;
    for (let i = 0; i < ring.length - 1; i++) {
        const [lng1, lat1] = ring[i];
        const [lng2, lat2] = ring[i + 1];
        total += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
    }
    const areaM2 = Math.abs((total * R * R) / 2);
    return areaM2 / 10_000;
}
// Body for PATCH /farms/me when the FARMER updates the parcel polygon.
export const UpdateFarmPolygonRequestSchema = z
    .object({
    gps_polygon: GeoPolygonSchema,
})
    .strict();
// Body for POST /eudr/dds.
export const GenerateDdsRequestSchema = z
    .object({
    sale_id: z.string().uuid(),
    // Click-accept declaration (V1). ECDSA digital signature lands in V2.
    accepted_declaration: z.literal(true),
})
    .strict();
// Response from POST /eudr/dds and GET /eudr/dds/:id.
// Response from PATCH /farms/me/polygon — just the farm id + when the
// server stamped the update so the mobile client can refresh local state.
export const UpdateFarmPolygonResponseSchema = z
    .object({
    farm_id: z.string().uuid(),
    validated_at: z.string().datetime({ offset: true }),
})
    .strict();
export const DueDiligenceStatementSchema = z.object({
    id: z.string().uuid(),
    reference_number: z.string(),
    sale_id: z.string().uuid(),
    farm_id: z.string().uuid(),
    animal_ids: z.array(z.string().uuid()),
    risk_level: EudrRiskLevelSchema,
    risk_notes: z.string().nullable(),
    pdf_url: z.string().nullable(),
    signed_at: z.string().datetime({ offset: true }),
    signed_payload_hash: z.string(),
    created_at: z.string().datetime({ offset: true }),
});
// Phase 25 iter-17 (M14) — list of DDS the BUYER received for sales they
// bought. Same shape as the seller-side listing but also includes a small
// "seller" + "sale" summary so the UI can render "Compraventa con
// Manolo Pérez · 03/05/2026" without an extra round-trip.
export const ReceivedDdsItemSchema = DueDiligenceStatementSchema.extend({
    seller_name: z.string(),
    sale_amount_cents: z.number().int().nonnegative().nullable(),
    sale_purchased_at: z.string().datetime({ offset: true }).nullable(),
});
export const ReceivedDdsListResponseSchema = z.object({
    items: z.array(ReceivedDdsItemSchema),
});
// `LCN-DDS-{YYYY}{MM}-{6-char-rand}` (e.g. LCN-DDS-202605-A3F9K2).
const REFERENCE_NUMBER_REGEX = /^LCN-DDS-\d{6}-[A-Z0-9]{6}$/;
export const DdsReferenceNumberSchema = z.string().regex(REFERENCE_NUMBER_REGEX);
export function isDdsReferenceNumber(value) {
    return REFERENCE_NUMBER_REGEX.test(value);
}
//# sourceMappingURL=eudr.js.map